/**
 * Which sign-in providers a SCREEN may draw: the ones this environment has
 * credentials for (the `signInProviders` value of the runtime config,
 * lib/runtime-config.ts), with one deliberate fallback.
 *
 * WHY A FALLBACK. The runtime config reaches the client three ways, and two
 * of them can hand a screen an EMPTY list that does not mean "nothing is
 * configured": the static apex `/login` is prerendered at image-build time
 * with no credentials in the environment, so its inline copy says `[]` until
 * the deferred `/api/runtime-config.js` repair has run (app/layout.tsx), and a
 * test or a 404 boundary may have no config at all (`EMPTY`). A sign-in
 * screen with no buttons is the whole funnel dead, so an empty list is read
 * as "not known here" and the three providers every environment has carried
 * since before this module existed are drawn. LinkedIn is the one provider
 * whose button depends on the live value, which is exactly the provider
 * section 5.6 of app-sign-in-and-onboarding.md says must show only where its
 * credentials are set.
 *
 * Client-safe and pure; the login body, the authorize page and the CLI
 * sign-in all read it, so none of them keeps its own copy of the rule.
 */

import { isSignInProvider, type SignInProvider } from '@/lib/auth/sign-in-providers';

/** The providers assumed configured when the runtime config has not said. */
export const ASSUMED_SIGN_IN_PROVIDERS: readonly SignInProvider[] = ['google', 'x', 'discord'];

export function availableSignInProviders(configured: readonly unknown[] | null | undefined): SignInProvider[] {
  const list = Array.isArray(configured) ? configured.filter(isSignInProvider) : [];
  return list.length ? list : [...ASSUMED_SIGN_IN_PROVIDERS];
}
